import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit, Signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatListModule } from '@angular/material/list';
import { MatSidenav, MatSidenavContent, MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import Keycloak from 'keycloak-js';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { filter } from 'rxjs/operators';

import { LayoutSettings } from '../../config/layout-settings';
import { BELOWMONITOR, MOBILE_VIEW, MONITOR_VIEW, TABLET_VIEW } from '../../constants/breakpoints.constants';
import { FeatureFlagDirective } from '../../directives/feature-flag/feature-flag.directive';
import { AuthService } from '../../services/auth/auth.service';
import { AuthStore } from '../../store/auth.store';
import { FarmStore } from '../../store/farm.store';
import { LayoutStore } from '../../store/layout.store';
import { MenuItem, MenuStore } from '../../store/menu.store';

import { TourDefinitionsService } from '../tour/tour-definitions.service';
import { TourStore } from '../tour/tour.service';
import { OnboardingModalComponent } from '../widgets/onboarding-modal/onboarding-modal.component';
import { TourFloatingPromptComponent } from '../widgets/tour-floating-prompt/tour-floating-prompt.component';
import { AppsPanelComponent } from './apps-panel/apps-panel.component';
import { AppBreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { HeaderComponent } from './header/header.component';
import { MobileBottomToolbarComponent } from './mobile-bottom-toolbar/mobile-bottom-toolbar.component';
import { AppNavItemComponent } from './sidebar/nav-item/nav-item.component';
import { SidebarComponent } from './sidebar/sidebar.component';

@Component({
  selector: 'app-layout',
  imports: [
    RouterModule,
    AppNavItemComponent,
    MatSidenavModule,
    MatListModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    HeaderComponent,
    AppBreadcrumbComponent,
    MobileBottomToolbarComponent,
    AppsPanelComponent,

    OnboardingModalComponent,
    TourFloatingPromptComponent,
    FeatureFlagDirective,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  public layoutStore = inject(LayoutStore);
  public menuStore = inject(MenuStore);
  private authStore = inject(AuthStore);
  private authService = inject(AuthService);
  private farmStore = inject(FarmStore);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  private keycloak = inject(Keycloak);
  private readonly tourDefinitionsService = inject(TourDefinitionsService);
  private readonly tourStore = inject(TourStore);

  public resView = false;
  public readonly allMenuItems = computed(() => {
    const sections = this.menuStore.visibleMenuSections();
    const allItems: MenuItem[] = [];

    sections.forEach(section => {
      if (section.children) {
        allItems.push(...section.children);
      }
    });

    return allItems;
  });

  constructor() {
    effect(() => {
      const isAuthenticated = this.authStore.isAuthenticated();
      console.log('[LayoutComponent] Auth effect - isAuthenticated:', isAuthenticated);

      if (!isAuthenticated) {
        // Check if there's an auth error - don't redirect, let error page show
        // The authError signal is set BEFORE navigation to /exception, so this works
        const authError = this.authService.authError();
        if (authError) {
          console.log('[LayoutComponent] Auth error detected - skipping login redirect:', authError);
          return;
        }

        // Also check current URL as fallback for direct /exception navigation
        const currentUrl = this.router.url;
        if (currentUrl.startsWith('/exception')) {
          console.log('[LayoutComponent] On exception page - skipping login redirect');
          return;
        }

        console.log('[LayoutComponent] Not authenticated - triggering login');
        this.keycloak.login({
          redirectUri: window.location.origin + this.router.url
        });
      }
    });
  }

  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private isTabletScreen = false;

  public layoutSettings: Signal<LayoutSettings> = this.layoutStore.layoutSettings;
  public sidenavOpened: Signal<boolean> = this.layoutStore.sidenavOpened;
  public sidenavCollapsed: Signal<boolean> = this.layoutStore.sidenavCollapsed;
  public isDarkTheme: Signal<boolean> = this.layoutStore.isDarkTheme;

  @ViewChild('leftsidenav')
  public sidenav!: MatSidenav;

  @ViewChild('content', { static: true })
  public content!: MatSidenavContent;

  private isMobileScreen = false;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  public ngOnInit(): void {
    this.menuStore.loadMenuData();

    this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        const isMobile = state.breakpoints[MOBILE_VIEW];
        const isTablet = state.breakpoints[TABLET_VIEW];
        const isDesktop = state.breakpoints[MONITOR_VIEW];

        // Mobile view: use overlay sidebar
        this.isMobileScreen = isMobile;
        this.isTabletScreen = isTablet;
        this.resView = isMobile;

        // Set sidebar state based on screen size
        if (isMobile) {
          // Mobile: sidebar closed by default, opens as overlay
          this.layoutStore.setLayoutSettings({
            sidenavOpened: false,
            sidenavCollapsed: false,
          });
        } else if (isTablet) {
          // Tablet: use desktop layout but sidebar closed by default
          this.layoutStore.setLayoutSettings({
            sidenavOpened: false,
            sidenavCollapsed: false,
          });
        } else if (isDesktop) {
          // Desktop: sidebar open by default
          this.layoutStore.setLayoutSettings({
            sidenavOpened: true,
            sidenavCollapsed: false,
          });
        }

        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
      });

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.content.scrollTo({ top: 0 });
    });
  }

  public openClosedMenu(): void {
    this.layoutStore.toggleSidenavCollapsed();
    this.resetCollapsedState();
  }

  public toggleCollapsed(): void {
    this.isContentWidthFixed = false;

    // On tablet, toggle open/close behavior
    // On desktop, toggle full/mini mode
    if (this.isTabletScreen) {
      this.layoutStore.toggleSidenavOpened();
    } else {
      this.layoutStore.toggleSidenavCollapsed();
    }

    this.resetCollapsedState();
  }

  public resetCollapsedState(timer = 400): void {
    setTimeout(() => this.layoutStore.setLayoutSettings({}), timer);
  }

  public onSidenavClosedStart(): void {
    this.isContentWidthFixed = false;
  }

  public onSidenavOpenedChange(isOpened: boolean): void {
    this.isCollapsedWidthFixed = !this.isOver;
    this.layoutStore.setLayoutSettings({ sidenavOpened: isOpened });
  }

  public onAppsPanelClose(): void {
    this.layoutStore.closeAppsPanel();
  }



  public onOnboardingModalClose(): void {
    this.layoutStore.closeOnboardingModal();
  }

  public onStartTour(): void {
    this.layoutStore.dismissTourPrompt();

    // Get recommended tour from definitions service
    const recommendedTour = this.tourDefinitionsService.recommendedTour();

    if (recommendedTour) {
      // Start the actual guided tour
      this.tourStore.startTour(recommendedTour);
    } else {
      // Fallback: open onboarding modal if no tour available
      this.layoutStore.openOnboardingModal();
    }
  }

  public onTourPromptDismiss(): void {
    this.layoutStore.dismissTourPrompt();
  }
}
